import * as Popover from "@radix-ui/react-popover"
import { AnimatePresence, motion, stagger, useMotionValue } from "motion/react"
import { ReactNode, useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { Checkbox } from "./Checkbox"
import { IconChevronDown } from "./Icon"
import { animate } from "motion"

type Props = {
  label?: string
  options: { value?: string; label: ReactNode }[]
  className?: string
  placeholder?: string
} & (
  | {
      multiple?: false
      value?: string
      onChange: (value: string) => void
    }
  | {
      multiple: true
      value?: string[]
      onChange: (value: string[]) => void
    }
)

export function Select(props: Props) {
  const [open, setOpen] = useState(false)
  const values = Array.isArray(props.value) ? props.value : [props.value]
  const selectedItems = values.map((value) => props.options.find((o) => o.value === value)).filter((o) => !!o)

  const hasSelectedItems = !!selectedItems.length

  let valueLabel: ReactNode = null
  if (selectedItems.length) {
    valueLabel = (
      <span className="border border-grey-mid/30 bg-grey-mid/5 text-black rounded-sm px-[0.2em]">
        {selectedItems[0].label}
      </span>
    )
  }
  if (selectedItems.length > 1) {
    valueLabel = (
      <span>
        {valueLabel}{" "}
        <span className="border border-grey-mid/30 bg-grey-mid/5 text-black rounded-sm px-[0.2em]">
          +{selectedItems.length - 1}
        </span>
      </span>
    )
  }

  const innerButtonRef = useRef<HTMLDivElement>(null)
  const width = useMotionValue("auto")

  useEffect(() => {
    const updateWidth = () => {
      if (innerButtonRef.current) {
        const w = innerButtonRef.current.offsetWidth
        if (width.get() === "auto") {
          width.set(w + "px")
        } else {
          animate(width, w + "px", { duration: 0.2 })
        }
      }
    }
    const ro = new ResizeObserver(updateWidth)
    ro.observe(innerButtonRef.current!)
    updateWidth()
    ro.observe(innerButtonRef.current!)
    return () => {
      ro.disconnect()
    }
  }, [])

  return (
    <div className={twMerge("inline-flex", props.className)}>
      <Popover.Root onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <motion.button
            className="rounded-sm border border-black h-button bg-white hover:bg-whiteLight/60 transition-colors _active:bg-whiteLight flex overflow-hidden"
            data-active={open ? true : undefined}
            style={{ width: width }}
          >
            <div ref={innerButtonRef} className="flex h-full items-center px-4 md:px-2 w-min">
              <span className="inline-flex px-1 gap-2 whitespace-nowrap">
                {valueLabel ? (
                  <>
                    <span className="text-grey-mid">{props.label}</span>
                    <span>{valueLabel}</span>
                  </>
                ) : (
                  <span>{props.label ?? props.placeholder}</span>
                )}
              </span>
              <IconChevronDown className="size-4" />
            </div>
          </motion.button>
        </Popover.Trigger>
        <Popover.Portal forceMount>
          <Popover.Content align="start" side="bottom" sideOffset={5} forceMount>
            <AnimatePresence>
              {open && (
                <motion.div
                  variants={{
                    open: { opacity: 1, y: 0, transition: { delayChildren: stagger(0.02) } },
                    closed: { opacity: 0, y: -5 },
                  }}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className="border bg-white rounded-sm p-3 overflow-auto"
                >
                  <div className="flex flex-col">
                    {props.options.map((option) => (
                      <motion.div
                        key={option.value}
                        variants={{
                          open: { opacity: 1 },
                          closed: { opacity: 0 },
                        }}
                      >
                        <Checkbox
                          checked={
                            props.value === option.value ||
                            (Array.isArray(props.value) && props.value.includes(option.value!))
                          }
                          className="py-[0.25em]"
                          onCheckedChange={(checked) => {
                            if (props.multiple) {
                              const newValue = checked
                                ? [...(props.value || []), option.value]
                                : (props.value || []).filter((v) => v !== option.value)
                              props.onChange?.(newValue as any)
                            } else {
                              props.onChange?.(checked ? option.value : (undefined as any))
                            }
                          }}
                          label={option.label}
                        />
                      </motion.div>
                    ))}
                    <AnimatePresence initial={false}>
                      {hasSelectedItems && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="flex items-center justify-end"
                        >
                          <button
                            className="type-label-s-caps mt-2 p-1"
                            onClick={() => props.onChange(props.multiple ? [] : (undefined as any))}
                          >
                            Reset
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )
}
